import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { getLifecycleErrorMessage } from 'src/graphql/entities/workflow/hooks/use-workflow-version-mutations';
import { SAVE_DRAFT_WORKFLOW_VERSION_DEFINITION_MUTATION } from 'src/graphql/entities/workflow/workflow.mutations';
import type {
  SavedDraftWorkflowVersionDefinition,
  SaveDraftWorkflowVersionDefinitionInput,
  SaveDraftWorkflowVersionDefinitionMutation,
} from 'src/graphql/entities/workflow/workflow.types';
import type { WorkflowDefinition } from 'src/pages/Settings/workflows/utils/workflow-definition';
import { useNotifications } from 'src/utils/hooks';

const SAVE_DRAFT_ERROR_MESSAGE = 'Failed to save the draft. Please try again.';

/**
 * Saves a draft's definition in place through the validating `saveDraftWorkflowVersionDefinition`.
 *
 * Not `updateWorkflowVersion`: that one skips validation and doesn't check the version is a draft,
 * so it could rewrite an active version.
 *
 * Definition errors come back as data (`saved: false`, `errors` per step and field) and are left
 * to the caller to put on the canvas. Anything else — no partner, the version was activated in the
 * meantime, a network failure — shows an alert and resolves to `null`.
 */
export function useSaveDraftWorkflowVersionDefinition({
  partnerId,
}: {
  partnerId?: number | null;
}): {
  saveDraftWorkflowVersionDefinition: (input: {
    workflowVersionId: string;
    definition: WorkflowDefinition;
  }) => Promise<SavedDraftWorkflowVersionDefinition | null>;
  isSaving: boolean;
} {
  const { showErrorAlert } = useNotifications();

  // The saved row comes back through `WorkflowVersionFields` and normalises over the cached one,
  // so the list and the header pick up the new definition without a refetch.
  const [saveMutation, { loading: isSaving }] = useMutation<
    SaveDraftWorkflowVersionDefinitionMutation,
    { input: SaveDraftWorkflowVersionDefinitionInput }
  >(SAVE_DRAFT_WORKFLOW_VERSION_DEFINITION_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(getLifecycleErrorMessage(error, SAVE_DRAFT_ERROR_MESSAGE));
    },
  });

  const saveDraftWorkflowVersionDefinition = useCallback(
    async ({
      workflowVersionId,
      definition,
    }: {
      workflowVersionId: string;
      definition: WorkflowDefinition;
    }) => {
      if (isNullish(partnerId)) {
        showErrorAlert('Partner not selected.');
        return null;
      }

      const { data } = await saveMutation({
        variables: {
          input: { workflowVersionId, partnerId, definition: JSON.stringify(definition) },
        },
      });

      return data?.saveDraftWorkflowVersionDefinition ?? null;
    },
    [partnerId, saveMutation, showErrorAlert],
  );

  return { saveDraftWorkflowVersionDefinition, isSaving };
}
